import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  MapPin, Plus, Edit2, Trash2, Save, X, Map, 
  Sprout, Maximize, Building2, Info
} from 'lucide-react';

interface Location {
  id: string;
  name: string;
  address: string;
  city: string;
  region: string;
  coordinates?: { lat: number; lng: number };
  totalArea: number;
  zones: Zone[];
  createdAt?: string;
}

interface Zone {
  id: string;
  name: string;
  type: 'greenhouse' | 'field' | 'orchard' | 'vineyard' | 'nursery';
  cropType: string;
  area: number;
  soilType?: string;
  irrigationType?: string;
  devices: number;
  status: 'active' | 'inactive' | 'maintenance';
}

const ZONE_TYPES = {
  greenhouse: { label: 'Serre', icon: '🏗️' },
  field: { label: 'Champ', icon: '🌾' },
  orchard: { label: 'Verger', icon: '🌳' },
  vineyard: { label: 'Vignoble', icon: '🍇' },
  nursery: { label: 'Pépinière', icon: '🌱' }
};

const CROP_TYPES = [
  'Agrumes', 'Oliviers', 'Tomates', 'Poivrons', 'Concombres', 
  'Fraises', 'Melons', 'Pommes de terre', 'Luzerne', 'Blé',
  'Raisins', 'Dattes', 'Plantes ornementales', 'Autre'
];

const SOIL_TYPES = [
  'Argileux', 'Sableux', 'Limoneux', 'Calcaire', 'Humifère', 'Mixte'
];

const IRRIGATION_TYPES = [
  'Goutte à goutte', 'Aspersion', 'Pivot', 'Gravitaire', 'Micro-aspersion'
];

const DemoLocations: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [showAddZone, setShowAddZone] = useState(false);
  const [newZone, setNewZone] = useState<Partial<Zone>>({
    type: 'field',
    status: 'active'
  });

  const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/demo`;

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 2000)); // Augmenté à 2s
      const response = await fetch(`${apiUrl}/locations`);
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      } else if (response.status === 429) {
        console.warn('Rate limited, retrying in 5 seconds...');
        setTimeout(fetchLocations, 5000);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error('Erreur lors du chargement des localisations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddZone = () => {
    if (!selectedLocation) return;
    
    if (!newZone.name || !newZone.cropType || !newZone.area) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const zone: Zone = {
      id: `zone-${Date.now()}`,
      name: newZone.name!,
      type: newZone.type as any,
      cropType: newZone.cropType!,
      area: newZone.area!,
      soilType: newZone.soilType,
      irrigationType: newZone.irrigationType,
      devices: 0,
      status: newZone.status as any
    };

    const updatedLocation = {
      ...selectedLocation,
      zones: [...selectedLocation.zones, zone],
      totalArea: selectedLocation.totalArea + zone.area
    };

    setSelectedLocation(updatedLocation);
    setLocations(locations.map(loc => 
      loc.id === selectedLocation.id ? updatedLocation : loc
    ));

    setNewZone({ type: 'field', status: 'active' });
    setShowAddZone(false);
    toast.success('Zone ajoutée avec succès');
  };

  const handleDeleteZone = (zoneId: string) => {
    if (!selectedLocation) return;
    
    const zone = selectedLocation.zones.find(z => z.id === zoneId);
    if (!zone) return;

    const updatedLocation = {
      ...selectedLocation,
      zones: selectedLocation.zones.filter(z => z.id !== zoneId),
      totalArea: selectedLocation.totalArea - zone.area
    };

    setSelectedLocation(updatedLocation);
    setLocations(locations.map(loc => 
      loc.id === selectedLocation.id ? updatedLocation : loc
    ));

    toast.success('Zone supprimée');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement des localisations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600 rounded-xl shadow-2xl p-6 sm:p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2 flex items-center gap-3">
              <MapPin className="h-10 w-10" />
              <span>Gestion des Localisations</span>
            </h1>
            <p className="text-green-50 text-lg">
              {locations.length} localisation{locations.length > 1 ? 's' : ''} • 
              {locations.reduce((sum, loc) => sum + loc.zones.length, 0)} zones
            </p>
          </div>
        </div>
      </div>

      {/* Locations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {locations.map(location => (
          <div
            key={location.id}
            onClick={() => setSelectedLocation(location)}
            className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 cursor-pointer transition-all hover:shadow-xl border-2 ${
              selectedLocation?.id === location.id
                ? 'border-emerald-500 ring-2 ring-emerald-300'
                : 'border-transparent'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  {location.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  📍 {location.city}, {location.region}
                </p>
              </div>
              {selectedLocation?.id === location.id && (
                <div className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-xs font-bold">
                  Sélectionné
                </div>
              )}
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Superficie totale</span>
                <span className="font-bold text-gray-900 dark:text-white">{location.totalArea} ha</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Zones</span>
                <span className="font-bold text-gray-900 dark:text-white">{location.zones.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Appareils</span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {location.zones.reduce((sum, zone) => sum + zone.devices, 0)}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap gap-2">
                {location.zones.slice(0, 3).map(zone => (
                  <span
                    key={zone.id}
                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs"
                  >
                    {ZONE_TYPES[zone.type]?.icon} {zone.name}
                  </span>
                ))}
                {location.zones.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                    +{location.zones.length - 3}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Zone Details */}
      {selectedLocation && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Map className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              <span>Zones de {selectedLocation.name}</span>
            </h2>
            <button
              onClick={() => setShowAddZone(true)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span>Ajouter une zone</span>
            </button>
          </div>

          {/* Zones List */}
          <div className="space-y-4">
            {selectedLocation.zones.map(zone => (
              <div key={zone.id} className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{ZONE_TYPES[zone.type]?.icon}</span>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                          {zone.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {ZONE_TYPES[zone.type]?.label}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      zone.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                      zone.status === 'inactive' ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' :
                      'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                    }`}>
                      {zone.status === 'active' ? '✓ Actif' : 
                       zone.status === 'inactive' ? '○ Inactif' : '⚙️ Maintenance'}
                    </span>
                    <button
                      onClick={() => handleDeleteZone(zone.id)}
                      className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Culture</p>
                    <p className="font-bold text-gray-900 dark:text-white flex items-center gap-1">
                      <Sprout className="h-4 w-4 text-green-600" />
                      {zone.cropType}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Superficie</p>
                    <p className="font-bold text-gray-900 dark:text-white flex items-center gap-1">
                      <Maximize className="h-4 w-4 text-blue-600" />
                      {zone.area} ha
                    </p>
                  </div>
                  {zone.soilType && (
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Type de sol</p>
                      <p className="font-bold text-gray-900 dark:text-white">
                        {zone.soilType}
                      </p>
                    </div>
                  )}
                  {zone.irrigationType && (
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Irrigation</p>
                      <p className="font-bold text-gray-900 dark:text-white">
                        {zone.irrigationType}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {selectedLocation.zones.length === 0 && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Map className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-semibold mb-2">Aucune zone configurée</p>
                <p className="text-sm">Ajoutez une première zone pour commencer</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Zone Modal */}
      {showAddZone && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                Ajouter une zone
              </h3>
              <button
                onClick={() => setShowAddZone(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Nom de la zone *
                </label>
                <input
                  type="text"
                  value={newZone.name || ''}
                  onChange={(e) => setNewZone({ ...newZone, name: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ex: Verger Nord"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Type d'installation *
                </label>
                <select
                  value={newZone.type}
                  onChange={(e) => setNewZone({ ...newZone, type: e.target.value as any })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {Object.entries(ZONE_TYPES).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value.icon} {value.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Type de culture *
                </label>
                <select
                  value={newZone.cropType || ''}
                  onChange={(e) => setNewZone({ ...newZone, cropType: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Sélectionner...</option>
                  {CROP_TYPES.map(crop => (
                    <option key={crop} value={crop}>{crop}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Superficie (hectares) *
                </label>
                <input
                  type="number"
                  value={newZone.area || ''}
                  onChange={(e) => setNewZone({ ...newZone, area: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ex: 2.5"
                  step="0.1"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Type de sol
                </label>
                <select
                  value={newZone.soilType || ''}
                  onChange={(e) => setNewZone({ ...newZone, soilType: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Sélectionner...</option>
                  {SOIL_TYPES.map(soil => (
                    <option key={soil} value={soil}>{soil}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Type d'irrigation
                </label>
                <select
                  value={newZone.irrigationType || ''}
                  onChange={(e) => setNewZone({ ...newZone, irrigationType: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Sélectionner...</option>
                  {IRRIGATION_TYPES.map(irrigation => (
                    <option key={irrigation} value={irrigation}>{irrigation}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Statut
                </label>
                <select
                  value={newZone.status}
                  onChange={(e) => setNewZone({ ...newZone, status: e.target.value as any })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="active">✓ Actif</option>
                  <option value="inactive">○ Inactif</option>
                  <option value="maintenance">⚙️ En maintenance</option>
                </select>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button
                onClick={() => setShowAddZone(false)}
                className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleAddZone}
                className="flex-1 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                <Save className="h-5 w-5" />
                <span>Ajouter</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoLocations;
